import { useState, useMemo } from 'react';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Link, useNavigate } from 'react-router-dom';
import { listingsApi, leadsApi, auth } from '@/services/api';
import { useApiData } from '@/hooks/useApiData';
import { mapListingToCard } from '@/services/listingMapper';
import { dashboardStats, leads, deals, reminders, relationshipManagers, properties } from '@/mocks/dashboard';
import type { ListingCard } from '@/services/listingMapper';

function Sparkline({ data, color, height = 40 }: { data: number[]; color: string; height?: number }) {
  const max = Math.max(...data, 1);
  const points = data.map((v, i) => `${(i / (data.length - 1)) * 100},${100 - (v / max) * 100}`).join(' ');
  const strokes: Record<string, string> = {
    accent: 'oklch(var(--accent-500))',
    primary: 'oklch(var(--primary-500))',
    secondary: 'oklch(var(--secondary-500))',
    rose: '#f43f5e',
    teal: '#14b8a6',
  };
  return (
    <svg viewBox="0 0 100 100" className="w-full" style={{ height }} preserveAspectRatio="none">
      <polyline points={points} fill="none" stroke={strokes[color] || strokes.accent} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function BarChart({ data, labels, color, height = 140 }: { data: number[]; labels: string[]; color: string; height?: number }) {
  const max = Math.max(...data, 1);
  const barWidth = 80 / data.length;
  const gap = (20 / data.length);
  const colors: Record<string, string> = {
    accent: 'oklch(var(--accent-500) / 0.7)',
    primary: 'oklch(var(--primary-500) / 0.7)',
    secondary: 'oklch(var(--secondary-500) / 0.7)',
    teal: 'rgba(20,184,166,0.6)',
    rose: 'rgba(244,63,94,0.6)',
  };
  return (
    <svg viewBox="0 0 100 100" className="w-full" style={{ height }} preserveAspectRatio="none">
      {data.map((v, i) => {
        const barH = (v / max) * 75;
        const x = 10 + i * (barWidth + gap);
        return (
          <g key={i}>
            <rect x={x} y={90 - barH} width={barWidth} height={barH} rx="1" fill={colors[color] || colors.accent} />
            <text x={x + barWidth / 2} y={96} textAnchor="middle" className="text-[5px]" fill="oklch(var(--foreground-400))" fontFamily="system-ui">{labels[i]}</text>
          </g>
        );
      })}
    </svg>
  );
}

function HorizontalBarChart({ data, labels, maxVal, color, height = 160 }: { data: number[]; labels: string[]; maxVal: number; color: string; height?: number }) {
  const colors: Record<string, string> = {
    accent: 'oklch(var(--accent-500) / 0.85)',
    primary: 'oklch(var(--primary-500) / 0.85)',
    secondary: 'oklch(var(--secondary-500) / 0.85)',
    teal: 'rgba(20,184,166,0.7)',
    rose: 'rgba(244,63,94,0.7)',
  };
  const barH = 12;
  const gap = 10;
  return (
    <svg viewBox="0 0 100 100" className="w-full" style={{ height }} preserveAspectRatio="none">
      {data.map((v, i) => {
        const y = 8 + i * (barH + gap);
        const w = (v / maxVal) * 70;
        return (
          <g key={i}>
            <text x="69" y={y + barH * 0.4} textAnchor="end" className="text-[4px]" fill="oklch(var(--foreground-400))" fontFamily="system-ui">{labels[i]}</text>
            <rect x="71" y={y} width={w} height={barH} rx="3" fill={colors[color] || colors.accent} />
            <text x={73 + w} y={y + barH * 0.4} className="text-[4px] font-semibold" fill="oklch(var(--foreground-950))" fontFamily="system-ui">{v}</text>
          </g>
        );
      })}
    </svg>
  );
}

function AreaChart({ data, labels, color, height = 140 }: { data: number[]; labels: string[]; color: string; height?: number }) {
  const max = Math.max(...data, 1);
  const stepX = 86 / (data.length - 1);
  const points = data.map((v, i) => `${10 + i * stepX},${90 - (v / max) * 80}`).join(' ');
  const areaPath = `M10,90 ${points} L${10 + (data.length - 1) * stepX},90 Z`;
  const colors: Record<string, string> = {
    accent: 'oklch(var(--accent-500) / 0.3)',
    primary: 'oklch(var(--primary-500) / 0.3)',
    secondary: 'oklch(var(--secondary-500) / 0.3)',
    teal: 'rgba(20,184,166,0.25)',
    rose: 'rgba(244,63,94,0.2)',
  };
  const strokes: Record<string, string> = {
    accent: 'oklch(var(--accent-500))',
    primary: 'oklch(var(--primary-500))',
    secondary: 'oklch(var(--secondary-500))',
    teal: '#14b8a6',
    rose: '#f43f5e',
  };
  return (
    <svg viewBox="0 0 100 100" className="w-full" style={{ height }} preserveAspectRatio="none">
      <path d={areaPath} fill={colors[color] || colors.accent} />
      <polyline points={points} fill="none" stroke={strokes[color] || strokes.accent} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      {data.map((v, i) => (
        <g key={i}>
          <circle cx={10 + i * stepX} cy={90 - (v / max) * 80} r="1.8" fill={strokes[color] || strokes.accent} />
          <text x={10 + i * stepX} y={96} textAnchor="middle" className="text-[4.5px]" fill="oklch(var(--foreground-400))" fontFamily="system-ui">{labels[i]}</text>
        </g>
      ))}
    </svg>
  );
}

const fallbackSparklines = {
  properties: [10, 15, 12, 18, 20, 22, 28],
  active: [5, 8, 10, 12, 15, 18, 20],
  leads: [3, 7, 5, 9, 11, 14, 17],
  revenue: [2, 4, 6, 8, 12, 15, 22],
  conversion: [1, 2, 3, 4, 5, 6, 7],
};

interface DashboardStats {
  total: number;
  available: number;
  leased: number;
  under_review: number;
  featured: number;
  total_rent_value: number;
  avg_size_acres: number;
  total_views: number;
}

interface LeadRow {
  id: number;
  name: string;
  email: string;
  phone: string;
  source: string;
  stage: string;
  status?: string;
  property_interest?: string | null;
  listing_title?: string | null;
  created_at: string;
  deal_value: number;
  assigned_rm: string;
  priority: string;
  city: string;
}

function statusBadge(stage: string) {
  const map: Record<string, string> = {
    'new': 'bg-blue-500/15 border-blue-500/25 text-blue-400',
    'contacted': 'bg-accent-100 border-accent-500/25 text-accent-700',
    'qualified': 'bg-teal-500/15 border-teal-500/25 text-teal-400',
    'site_visit': 'bg-secondary-100 border-secondary-200 text-secondary-700',
    'negotiation': 'bg-primary-100 border-primary-200 text-primary-700',
    'converted': 'bg-green-500/15 border-green-500/25 text-green-400',
    'lost': 'bg-rose-500/15 border-rose-500/25 text-rose-400',
    'captured': 'bg-background-200/50 border-background-300/30 text-foreground-500',
  };
  return map[stage] || 'bg-background-200/50 border-background-300/30 text-foreground-500';
}

function statusLabel(stage: string) {
  const map: Record<string, string> = {
    'new': 'New Lead',
    'contacted': 'Contacted',
    'qualified': 'Qualified',
    'site_visit': 'Site Visit',
    'negotiation': 'Negotiation',
    'converted': 'Converted',
    'lost': 'Lost',
    'captured': 'Captured',
  };
  return map[stage] || stage;
}

function propertyStatusBadge(status: string) {
  const map: Record<string, string> = {
    'available': 'bg-primary-500/15 border-primary-500/25 text-primary-700',
    'under_review': 'bg-accent-100 border-accent-500/25 text-accent-700',
    'leased': 'bg-teal-500/15 border-teal-500/25 text-teal-400',
    'sold': 'bg-rose-500/15 border-rose-500/25 text-rose-400',
    'off_market': 'bg-background-200/50 border-background-300/30 text-foreground-500',
  };
  return map[status] || map.available;
}

function formatINR(val: number): string {
  if (val >= 10000000) return '\u20B9' + (val / 10000000).toFixed(1) + 'Cr';
  if (val >= 100000) return '\u20B9' + (val / 100000).toFixed(1) + 'L';
  if (val >= 1000) return '\u20B9' + (val / 1000).toFixed(0) + 'K';
  return '\u20B9' + val.toFixed(0);
}

function formatDate(dateStr: string): string {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

// --- Mock analytics computations (static, computed once at module level) ---
const revenueMonths = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const mockRevenueData = [2.8, 3.1, 3.5, 3.2, 4.0, 4.5, 5.2, 4.8, 5.5, 5.0, 5.8, 6.2];
const mockLeadsTrend = [42, 48, 55, 51, 62, 68, 72, 65, 78, 82, 88, 94];

const propertyTypeDistribution = (() => {
  const types: Record<string, number> = {};
  properties.forEach(p => { types[p.type] = (types[p.type] || 0) + 1; });
  return Object.entries(types).sort((a, b) => b[1] - a[1]);
})();

const leadSourceBreakdown = (() => {
  const sources: Record<string, number> = {};
  leads.forEach(l => { sources[l.source] = (sources[l.source] || 0) + 1; });
  return Object.entries(sources).sort((a, b) => b[1] - a[1]);
})();

const rmPerformance = relationshipManagers.map(rm => ({
  name: rm.name,
  activeLeads: rm.activeLeads,
  closedDeals: rm.closedDeals,
  conversionRate: rm.conversionRate,
}));

const pipelineFunnel = (() => {
  const stages = ['new', 'contacted', 'qualified', 'site_visit', 'negotiation', 'converted'];
  return stages.map(stage => ({
    stage: statusLabel(stage),
    count: leads.filter(l => l.status.toLowerCase() === stage).length,
  }));
})();

export default function DashboardPage() {
  const isAuthed = auth.isAuthenticated();
  const navigate = useNavigate();
  const [activeAnalyticsTab, setActiveAnalyticsTab] = useState<'overview' | 'revenue' | 'leads'>('overview');

  const { data: statsData, loading: statsLoading } = useApiData<DashboardStats>(
    () => listingsApi.stats().then(r => r.data as unknown as DashboardStats),
    [isAuthed]
  );

  const { data: leadsData, loading: leadsLoading } = useApiData<{ data: LeadRow[]; pagination: { total: number } }>(
    () => leadsApi.list({ per_page: 10, page: 1 }).then(r => r as unknown as { data: LeadRow[]; pagination: { total: number } }),
    [isAuthed]
  );

  const { data: propsData, loading: propsLoading } = useApiData<{ data: Record<string, unknown>[]; pagination: { total: number } }>(
    () => listingsApi.list({ per_page: 5, page: 1, status: 'available' }).then(r => r as unknown as { data: Record<string, unknown>[] }),
    [isAuthed]
  );

  const stats = statsData;
  const leadsList = leadsData?.data || [];
  const propsList = propsData?.data || [];
  const featuredProps: ListingCard[] = useMemo(
    () => propsList.map(p => mapListingToCard(p as Parameters<typeof mapListingToCard>[0])),
    [propsList]
  );

  const statCards = [
    { label: 'Total Properties', value: stats?.total || dashboardStats.totalProperties, icon: 'ri-building-4-line', color: 'accent', change: dashboardStats.statsChange.totalProperties, sparkline: fallbackSparklines.properties, navigateTo: '/dashboard/properties', filterState: null as Record<string, string> | null },
    { label: 'Active Listings', value: stats?.available || dashboardStats.activeListings, icon: 'ri-checkbox-circle-line', color: 'primary', change: dashboardStats.statsChange.activeListings, sparkline: fallbackSparklines.active, navigateTo: '/dashboard/properties', filterState: { status: 'Active' } },
    { label: 'Leads This Month', value: leadsData?.pagination?.total || dashboardStats.totalLeadsThisMonth, icon: 'ri-user-add-line', color: 'secondary', change: dashboardStats.statsChange.totalLeadsThisMonth, sparkline: fallbackSparklines.leads, navigateTo: '/dashboard/crm', filterState: null },
    { label: 'Revenue', value: formatINR(stats?.total_rent_value || dashboardStats.revenue), icon: 'ri-money-rupee-circle-line', color: 'teal', change: dashboardStats.statsChange.revenue, sparkline: fallbackSparklines.revenue, navigateTo: '/dashboard/transactions', filterState: null },
    { label: 'Conversion Rate', value: dashboardStats.conversionRate + '%', icon: 'ri-line-chart-line', color: 'rose', change: dashboardStats.statsChange.conversionRate, sparkline: fallbackSparklines.conversion, navigateTo: '/dashboard/crm', filterState: { tab: 'analytics' } },
  ];

  const isLoading = statsLoading || leadsLoading || propsLoading;

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Welcome Banner */}
        <div className="relative overflow-hidden rounded-2xl bg-background-50 border border-background-200/60 p-6 md:p-8">
          <div className="absolute top-0 right-0 w-72 h-72 bg-accent-50 rounded-full blur-3xl -translate-y-1/2 translate-x-1/4" />
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-primary-500/5 rounded-full blur-3xl translate-y-1/2 -translate-x-1/4" />
          <div className="relative flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-accent-700 bg-accent-500/10 border border-accent-200 px-3 py-1 rounded-full">
                  <span className="w-1.5 h-1.5 rounded-full bg-accent-500 animate-pulse" />
                  Live Dashboard
                </span>
              </div>
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-background-950 mb-2">
                Welcome back, <span className="text-accent-700">Admin</span>
              </h1>
              <p className="text-sm text-background-400 max-w-lg">
                Here&apos;s what&apos;s happening with your properties and leads today.
              </p>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <Link to="/dashboard/properties" className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-accent-500 text-white text-sm font-semibold hover:bg-accent-400 transition-colors cursor-pointer whitespace-nowrap">
                <i className="ri-add-circle-line" /> Add Property
              </Link>
              <Link to="/dashboard/crm" className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl border border-background-200/60 bg-background-200 text-background-950 text-sm font-semibold hover:border-accent-200 transition-colors cursor-pointer whitespace-nowrap">
                <i className="ri-user-add-line" /> Add Lead
              </Link>
            </div>
          </div>
        </div>

        {isLoading ? (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="rounded-xl border border-background-200/60 bg-white p-5 animate-pulse">
                <div className="h-3 bg-background-800 rounded w-1/2 mb-3" />
                <div className="h-7 bg-background-800 rounded w-2/3 mb-2" />
                <div className="h-8 bg-background-800 rounded w-full" />
              </div>
            ))}
          </div>
        ) : (
          <>
            {/* Stat Cards Row */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              {statCards.map((card) => (
                <div
                  key={card.label}
                  onClick={() => navigate(card.navigateTo, { state: card.filterState ? { dashboardFilter: card.filterState } : undefined })}
                  className="rounded-xl border bg-white border-background-200/60 p-4 transition-all hover:border-accent-200 hover:-translate-y-0.5 cursor-pointer group"
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[10px] font-semibold text-foreground-500 uppercase tracking-wider group-hover:text-accent-700 transition-colors">{card.label}</span>
                    <span className={`w-7 h-7 rounded-lg flex items-center justify-center ${
                      card.color === 'accent' ? 'bg-accent-100 text-accent-700' :
                      card.color === 'primary' ? 'bg-primary-500/15 text-primary-700' :
                      card.color === 'secondary' ? 'bg-secondary-500/15 text-secondary-700' :
                      card.color === 'teal' ? 'bg-teal-500/15 text-teal-400' :
                      'bg-rose-500/15 text-rose-400'
                    }`}>
                      <i className={`${card.icon} text-xs`} />
                    </span>
                  </div>
                  <p className="font-heading text-xl font-bold text-foreground-950 mb-1">{card.value}</p>
                  <div className="flex items-center gap-2">
                    <span className={`text-[10px] font-semibold ${card.change >= 0 ? 'text-teal-400' : 'text-rose-400'}`}>
                      {card.change >= 0 ? '+' : ''}{card.change}%
                    </span>
                    <span className="text-[10px] text-foreground-500">vs last month</span>
                  </div>
                  <div className="mt-2">
                    <Sparkline data={card.sparkline} color={card.color} height={32} />
                  </div>
                </div>
              ))}
            </div>

            {/* Recent Leads + Properties */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="font-heading text-lg font-semibold text-foreground-950">Recent Leads</h2>
                    <p className="text-xs text-foreground-500">Last 10 lead inquiries</p>
                  </div>
                  <Link to="/dashboard/crm" className="flex items-center gap-1 text-xs font-semibold text-accent-700 hover:text-accent-300 whitespace-nowrap">
                    View All <i className="ri-arrow-right-line" />
                  </Link>
                </div>
                <div className="rounded-xl border border-background-200/60 bg-white overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-background-200/60">
                          <th className="text-left text-[10px] font-semibold text-foreground-500 uppercase tracking-wider px-4 py-3">Name</th>
                          <th className="text-left text-[10px] font-semibold text-foreground-500 uppercase tracking-wider px-4 py-3">Source</th>
                          <th className="text-left text-[10px] font-semibold text-foreground-500 uppercase tracking-wider px-4 py-3">Property Interest</th>
                          <th className="text-left text-[10px] font-semibold text-foreground-500 uppercase tracking-wider px-4 py-3">Status</th>
                          <th className="text-left text-[10px] font-semibold text-foreground-500 uppercase tracking-wider px-4 py-3">Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {leadsList.length === 0 ? (
                          <tr>
                            <td colSpan={5} className="px-4 py-8 text-center text-sm text-foreground-500">
                              No leads yet. New leads will appear here as they come in.
                            </td>
                          </tr>
                        ) : (
                          leadsList.map((lead) => (
                            <tr key={lead.id} className="border-b border-background-200/50 hover:bg-background-200/80 transition-colors">
                              <td className="px-4 py-3 text-sm text-foreground-950 font-medium whitespace-nowrap">{lead.name}</td>
                              <td className="px-4 py-3">
                                <span className="inline-flex items-center gap-1 text-xs text-foreground-500">
                                  <i className={`${lead.source === 'website' ? 'ri-global-line' : lead.source === 'whatsapp' ? 'ri-whatsapp-line' : 'ri-mail-line'} text-accent-700`} />
                                  {lead.source || 'website'}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-xs text-foreground-500 max-w-[180px] truncate">{lead.property_interest || lead.listing_title || 'General Inquiry'}</td>
                              <td className="px-4 py-3">
                                <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${statusBadge(lead.stage || lead.status || 'new')} whitespace-nowrap`}>
                                  {statusLabel(lead.stage || lead.status || 'new')}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-xs text-foreground-500 whitespace-nowrap">{formatDate(lead.created_at)}</td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div className="space-y-3">
                  <h2 className="font-heading text-lg font-semibold text-foreground-950">Quick Actions</h2>
                  <div className="space-y-2">
                    {[
                      { label: 'Add Property', icon: 'ri-add-circle-line', href: '/dashboard/properties', desc: 'List a new property', color: 'accent' },
                      { label: 'Add Lead', icon: 'ri-user-add-line', href: '/dashboard/crm', desc: 'Capture a new lead', color: 'primary' },
                      { label: 'Export Report', icon: 'ri-file-download-line', href: '/dashboard/reports', desc: 'Download analytics', color: 'secondary' },
                    ].map((action) => (
                      <Link key={action.label} to={action.href} className="flex items-center gap-3 p-3 rounded-xl border border-background-200/60 bg-white hover:border-accent-200 transition-all cursor-pointer group">
                        <span className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
                          action.color === 'accent' ? 'bg-accent-100 text-accent-700' :
                          action.color === 'primary' ? 'bg-primary-500/15 text-primary-700' :
                          'bg-secondary-500/15 text-secondary-700'
                        }`}>
                          <i className={action.icon} />
                        </span>
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-foreground-950">{action.label}</p>
                          <p className="text-[11px] text-foreground-500">{action.desc}</p>
                        </div>
                        <i className="ri-arrow-right-s-line text-foreground-500 group-hover:text-accent-700 ml-auto" />
                      </Link>
                    ))}
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h2 className="font-heading text-lg font-semibold text-foreground-950">Recent Properties</h2>
                    <Link to="/dashboard/properties" className="text-xs font-semibold text-accent-700 hover:text-accent-300 whitespace-nowrap">View All</Link>
                  </div>
                  <div className="space-y-2">
                    {featuredProps.length === 0 ? (
                      <p className="text-xs text-foreground-500 py-4 text-center">No active properties yet.</p>
                    ) : (
                      featuredProps.map((p) => (
                        <div key={p.id} className="flex items-center gap-3 p-3 rounded-xl border border-background-200/60 bg-white hover:border-accent-200 transition-colors">
                          <div className="w-12 h-12 rounded-lg bg-background-200/60 overflow-hidden flex items-center justify-center flex-shrink-0">
                            {p.images.length > 0 ? (
                              <img src={p.images[0]} alt="" className="w-full h-full object-cover" />
                            ) : (
                              <i className="ri-building-line text-background-400 text-lg" />
                            )}
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium text-foreground-950 truncate">{p.title}</p>
                            <p className="text-[11px] text-foreground-500">{p.city} &middot; {p.sizeDisplay}</p>
                            <p className="text-sm font-bold text-foreground-950 mt-0.5">{p.expectedRentDisplay}</p>
                          </div>
                          <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full border ${propertyStatusBadge(p.status.toLowerCase().replace(' ', '_'))} whitespace-nowrap`}>
                            {p.status}
                          </span>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* ===== ANALYTICS SECTION ===== */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="font-heading text-lg font-semibold text-foreground-950">Analytics & Insights</h2>
                  <p className="text-xs text-foreground-500">Real-time performance metrics and trends</p>
                </div>
                <div className="flex items-center gap-1 bg-background-50 rounded-xl p-1">
                  {[
                    { key: 'overview', label: 'Overview', icon: 'ri-dashboard-line' },
                    { key: 'revenue', label: 'Revenue', icon: 'ri-money-rupee-circle-line' },
                    { key: 'leads', label: 'Leads', icon: 'ri-user-search-line' },
                  ].map(tab => (
                    <button
                      key={tab.key}
                      onClick={() => setActiveAnalyticsTab(tab.key as typeof activeAnalyticsTab)}
                      className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs whitespace-nowrap cursor-pointer transition-all ${
                        activeAnalyticsTab === tab.key
                          ? 'bg-accent-100 text-accent-700 font-semibold'
                          : 'text-foreground-500 hover:text-foreground-950'
                      }`}
                    >
                      <i className={`${tab.icon} text-[11px]`} /> {tab.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Overview Tab */}
              {activeAnalyticsTab === 'overview' && (
                <div className="space-y-4">
                  {/* Top row: Revenue Trend + Property Types */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <div className="rounded-xl border border-background-200/60 bg-white p-5">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="text-sm font-semibold text-foreground-950">Monthly Revenue Trend</h3>
                        <span className="text-[10px] text-teal-400 bg-teal-500/10 px-2 py-0.5 rounded-full">+18% YoY</span>
                      </div>
                      <BarChart data={mockRevenueData} labels={['J','F','M','A','M','J','J','A','S','O','N','D']} color="accent" height={140} />
                      <div className="flex items-center justify-between mt-2">
                        <span className="text-[10px] text-foreground-500">Total: {formatINR(dashboardStats.revenue * 1000)}</span>
                        <span className="text-[10px] text-teal-400 font-semibold">Target: 110% achieved</span>
                      </div>
                    </div>

                    <div className="rounded-xl border border-background-200/60 bg-white p-5">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="text-sm font-semibold text-foreground-950">Property Type Distribution</h3>
                        <span className="text-[10px] text-foreground-500">{properties.length} total</span>
                      </div>
                      <HorizontalBarChart
                        data={propertyTypeDistribution.map(([, c]) => c)}
                        labels={propertyTypeDistribution.map(([t]) => t)}
                        maxVal={Math.max(...propertyTypeDistribution.map(([, c]) => c), 1) + 2}
                        color="primary"
                        height={150}
                      />
                    </div>
                  </div>

                  {/* Bottom row: Lead Sources + Pipeline Funnel + RM Performance */}
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                    <div className="rounded-xl border border-background-200/60 bg-white p-5">
                      <h3 className="text-sm font-semibold text-foreground-950 mb-3">Lead Sources</h3>
                      <div className="space-y-2.5">
                        {leadSourceBreakdown.map(([source, count], i) => {
                          const colors = ['bg-accent-500', 'bg-primary-500', 'bg-secondary-500', 'bg-teal-500', 'bg-rose-500', 'bg-blue-500'];
                          const pct = Math.round((count / leads.length) * 100);
                          return (
                            <div key={source} className="flex items-center gap-2.5">
                              <span className={`w-2.5 h-2.5 rounded-sm ${colors[i % colors.length]} flex-shrink-0`} />
                              <span className="text-xs text-foreground-500 flex-1">{source}</span>
                              <div className="w-24 h-1.5 bg-background-200/60 rounded-full overflow-hidden">
                                <div className={`h-full rounded-full ${colors[i % colors.length]}`} style={{ width: `${pct}%` }} />
                              </div>
                              <span className="text-[10px] font-semibold text-foreground-950 w-7 text-right">{count}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    <div className="rounded-xl border border-background-200/60 bg-white p-5">
                      <h3 className="text-sm font-semibold text-foreground-950 mb-3">Pipeline Funnel</h3>
                      <div className="space-y-1.5">
                        {pipelineFunnel.map((stage, i) => {
                          const maxCount = Math.max(...pipelineFunnel.map(s => s.count), 1);
                          const pct = Math.round((stage.count / maxCount) * 100);
                          const colors = ['bg-blue-500/50', 'bg-accent-500/60', 'bg-accent-500/70', 'bg-teal-500/60', 'bg-green-500/60', 'bg-green-500/70'];
                          return (
                            <div key={stage.stage} className="flex items-center gap-2">
                              <span className="text-[10px] text-foreground-500 w-20 text-right flex-shrink-0">{stage.stage}</span>
                              <div className="flex-1 h-5 bg-background-200/60 rounded overflow-hidden">
                                <div
                                  className={`h-full rounded ${colors[i % colors.length]} flex items-center justify-end pr-2 transition-all`}
                                  style={{ width: `${pct}%`, minWidth: stage.count > 0 ? '24px' : '0' }}
                                >
                                  {stage.count > 0 && <span className="text-[9px] font-semibold text-white">{stage.count}</span>}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      <div className="mt-3 pt-3 border-t border-background-200/60 flex items-center justify-between">
                        <span className="text-[10px] text-foreground-500">Pipeline Value</span>
                        <span className="text-xs font-bold text-accent-700">{formatINR(deals.reduce((sum, d) => sum + d.dealValue, 0))}</span>
                      </div>
                    </div>

                    <div className="rounded-xl border border-background-200/60 bg-white p-5">
                      <h3 className="text-sm font-semibold text-foreground-950 mb-3">RM Performance</h3>
                      <div className="space-y-3">
                        {rmPerformance.map((rm) => (
                          <div key={rm.name} className="space-y-1.5">
                            <div className="flex items-center justify-between">
                              <span className="text-xs font-medium text-foreground-950">{rm.name}</span>
                              <span className={`text-[10px] font-semibold ${rm.conversionRate >= 20 ? 'text-teal-400' : rm.conversionRate >= 15 ? 'text-accent-700' : 'text-foreground-500'}`}>{rm.conversionRate}%</span>
                            </div>
                            <div className="flex items-center gap-3">
                              <div className="flex-1 h-1.5 bg-background-200/60 rounded-full overflow-hidden">
                                <div
                                  className={`h-full rounded-full ${rm.conversionRate >= 20 ? 'bg-teal-500' : rm.conversionRate >= 15 ? 'bg-accent-500' : 'bg-secondary-500'}`}
                                  style={{ width: `${Math.min(rm.conversionRate * 3, 100)}%` }}
                                />
                              </div>
                              <div className="flex items-center gap-2 text-[10px] text-foreground-500">
                                <span>{rm.activeLeads} active</span>
                                <span className="text-foreground-500">&middot;</span>
                                <span>{rm.closedDeals} won</span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Reminders / Today's Tasks */}
                  <div className="rounded-xl border border-background-200/60 bg-white p-5">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-sm font-semibold text-foreground-950">Today&apos;s Reminders</h3>
                      <Link to="/dashboard/crm" className="text-[10px] font-semibold text-accent-700 hover:text-accent-300">View All</Link>
                    </div>
                    <div className="space-y-2">
                      {reminders.slice(0, 5).map((r) => (
                        <div key={r.id} className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
                          r.status === 'Overdue' ? 'bg-rose-500/5 border border-rose-500/15' :
                          r.status === 'Snoozed' ? 'bg-background-200/50' :
                          'bg-background-200'
                        }`}>
                          <span className={`w-2 h-2 rounded-full flex-shrink-0 ${
                            r.priority === 'High' ? 'bg-rose-500' : r.priority === 'Medium' ? 'bg-accent-500' : 'bg-secondary-500'
                          }`} />
                          <div className="flex-1 min-w-0">
                            <p className="text-xs text-foreground-950 truncate">{r.text}</p>
                            <p className="text-[10px] text-foreground-500">{r.leadName} &middot; {new Date(r.dueDateTime).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}</p>
                          </div>
                          {r.status === 'Overdue' && (
                            <span className="text-[9px] font-semibold text-rose-400 bg-rose-500/10 px-1.5 py-0.5 rounded-full flex-shrink-0">Overdue</span>
                          )}
                          {r.status === 'Snoozed' && (
                            <span className="text-[9px] font-semibold text-foreground-500 bg-background-200/60 px-1.5 py-0.5 rounded-full flex-shrink-0">Snoozed</span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Revenue Tab */}
              {activeAnalyticsTab === 'revenue' && (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                    <div className="lg:col-span-3 rounded-xl border border-background-200/60 bg-white p-5">
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <h3 className="text-sm font-semibold text-foreground-950">Revenue Growth (12-Month)</h3>
                          <p className="text-[10px] text-foreground-500">Monthly revenue in lakhs</p>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="flex items-center gap-1.5 text-[10px] text-foreground-500"><span className="w-2 h-2 rounded-sm bg-accent-500/70" /> Revenue</span>
                          <span className="flex items-center gap-1.5 text-[10px] text-foreground-500"><span className="w-2 h-2 rounded-sm bg-teal-500/60" /> Target</span>
                        </div>
                      </div>
                      <BarChart data={mockRevenueData} labels={revenueMonths} color="accent" height={200} />
                      <div className="grid grid-cols-4 gap-3 mt-4">
                        {[
                          { label: 'Total Revenue', value: formatINR(dashboardStats.revenue * 1000), change: '+15.3%', color: 'teal' },
                          { label: 'Avg Deal Size', value: formatINR(450000), change: '+8.2%', color: 'accent' },
                          { label: 'Revenue / Property', value: formatINR(40000), change: '+5.1%', color: 'primary' },
                          { label: 'Outstanding', value: formatINR(285000), change: '-3.7%', color: 'rose' },
                        ].map(stat => (
                          <div key={stat.label} className="p-3 rounded-lg bg-background-200 border border-background-200/60">
                            <p className="text-[10px] text-foreground-500 uppercase tracking-wider">{stat.label}</p>
                            <p className="text-sm font-bold text-foreground-950 mt-0.5">{stat.value}</p>
                            <p className={`text-[10px] font-semibold ${stat.change.startsWith('+') ? 'text-teal-400' : 'text-rose-400'}`}>{stat.change}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Leads Tab */}
              {activeAnalyticsTab === 'leads' && (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <div className="rounded-xl border border-background-200/60 bg-white p-5">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="text-sm font-semibold text-foreground-950">Monthly Lead Volume</h3>
                        <span className="text-[10px] text-teal-400 bg-teal-500/10 px-2 py-0.5 rounded-full">+24% YoY</span>
                      </div>
                      <AreaChart data={mockLeadsTrend} labels={['J','F','M','A','M','J','J','A','S','O','N','D']} color="accent" height={150} />
                    </div>

                    <div className="rounded-xl border border-background-200/60 bg-white p-5">
                      <h3 className="text-sm font-semibold text-foreground-950 mb-3">Deal Pipeline</h3>
                      <div className="space-y-2.5">
                        {deals.map(deal => (
                          <div key={deal.id} className="flex items-center gap-3 p-2.5 rounded-lg bg-background-200 border border-background-200/50">
                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                              deal.stage === 'Closed' ? 'bg-green-500/15 text-green-400' :
                              deal.stage.includes('Drafted') ? 'bg-teal-500/15 text-teal-400' :
                              deal.stage.includes('Proposal') ? 'bg-accent-100 text-accent-700' :
                              'bg-primary-500/15 text-primary-700'
                            }`}>
                              <i className={`text-xs ${deal.stage === 'Closed' ? 'ri-check-line' : deal.stage.includes('Drafted') ? 'ri-file-text-line' : deal.stage.includes('Proposal') ? 'ri-mail-send-line' : 'ri-scales-line'}`} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-medium text-foreground-950 truncate">{deal.leadName}</p>
                              <p className="text-[10px] text-foreground-500 truncate">{deal.propertyTitle}</p>
                            </div>
                            <div className="text-right flex-shrink-0">
                              <p className="text-xs font-bold text-foreground-950">{formatINR(deal.dealValue)}</p>
                              <p className="text-[10px] text-foreground-500">{deal.stage}</p>
                            </div>
                            <div className="flex-shrink-0">
                              <span className={`text-[10px] font-semibold ${deal.probability >= 70 ? 'text-teal-400' : deal.probability >= 40 ? 'text-accent-700' : 'text-foreground-500'}`}>
                                {deal.probability}%
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                      <div className="mt-3 pt-3 border-t border-background-200/60 flex items-center justify-between">
                        <span className="text-[10px] text-foreground-500">{deals.length} active deals</span>
                        <Link to="/dashboard/crm" className="text-[10px] font-semibold text-accent-700 hover:text-accent-300">Manage Pipeline</Link>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </DashboardLayout>
  );
}